import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { PhoneInput, isValidPhoneNumber } from "@/components/ui/phone-input";
import { submitContact } from "../lib/api";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      toast.error("Please enter a valid phone number for the selected country.");
      return;
    }
    setLoading(true);
    setSuccess(null);
    try {
      const saved = await submitContact(form);
      toast.success("Message received. We'll be in touch within one business day.");
      setSuccess(saved);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-section py-16 md:py-24">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">Contact</span>
          <h1 className="mt-3 max-w-3xl text-4xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Let's talk about your <span className="brand-gradient-text">workflow</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            Whether you're evaluating PixTraya, looking for a consulting engagement, or exploring a partnership — we'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="container-section py-16 md:py-24">
        <div className="grid gap-10 lg:grid-cols-5">
          {/* Info */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-semibold text-slate-900">Get in touch</h2>
            <p className="mt-2 text-sm text-slate-600">Reach us directly or fill out the form.</p>

            <ul className="mt-8 space-y-5">
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#0046B5]/10 text-[#0046B5]"><Mail className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</p>
                  <a href="mailto:sales@ortelly.com" data-testid="contact-info-email" className="text-sm font-medium text-slate-900 hover:text-[#0046B5]">sales@ortelly.com</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#0046B5]/10 text-[#0046B5]"><Phone className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</p>
                  <p className="text-sm font-medium text-slate-900">+91 9985646404</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#0046B5]/10 text-[#0046B5]"><MapPin className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Office</p>
                  <p className="text-sm font-medium text-slate-900">Hyderabad, Telangana<br />India</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#0046B5]/10 text-[#0046B5]"><Clock className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours</p>
                  <p className="text-sm font-medium text-slate-900">Mon – Fri, 10:00 – 19:00 IST</p>
                </div>
              </li>
            </ul>

            <div className="mt-8 overflow-hidden rounded-md border border-slate-200">
              <iframe
                title="Ortelly Systems — Office Location"
                src="https://www.google.com/maps?q=Hyderabad,+Telangana,+India&output=embed"
                width="100%"
                height="240"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="block"
                data-testid="contact-map"
              />
            </div>
          </div>

          {/* Form */}
          <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm md:p-8 lg:col-span-3">
            <h2 className="text-2xl font-semibold text-slate-900">Send us a message</h2>
            <p className="mt-1 text-sm text-slate-600">We reply within one business day.</p>

            {success && (
              <Alert
                data-testid="contact-success-card"
                className="mt-6 border-[#00C4B5]/40 bg-[#00C4B5]/10 text-slate-900"
              >
                <CheckCircle2 className="h-5 w-5 text-[#00C4B5]" />
                <AlertTitle className="font-[Outfit] text-base font-semibold text-slate-900">
                  Message received — saved to our database.
                </AlertTitle>
                <AlertDescription>
                  <p className="mt-1 text-slate-700">
                    Thanks <strong>{success.name}</strong>. Your message is safely stored and we'll reply to <strong>{success.email}</strong> within one business day.
                  </p>
                  <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs text-slate-600">
                    <dt className="font-semibold">Reference</dt>
                    <dd className="font-mono">{success.id}</dd>
                    <dt className="font-semibold">Received</dt>
                    <dd>{new Date(success.created_at).toLocaleString()}</dd>
                    <dt className="font-semibold">Email notified</dt>
                    <dd>{success.email_sent ? "Yes" : "Stored in DB (email delivery pending)"}</dd>
                  </dl>
                  <button
                    type="button"
                    onClick={() => setSuccess(null)}
                    data-testid="contact-success-dismiss"
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0046B5] hover:underline"
                  >
                    <X className="h-3 w-3" /> Dismiss
                  </button>
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="contact-form">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="c-name">Full name *</Label>
                  <Input id="c-name" name="name" value={form.name} onChange={handleChange} data-testid="contact-input-name" required className="mt-1.5" placeholder="Your name" />
                </div>
                <div>
                  <Label htmlFor="c-email">Email *</Label>
                  <Input id="c-email" type="email" name="email" value={form.email} onChange={handleChange} data-testid="contact-input-email" required className="mt-1.5" placeholder="you@company.com" />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="c-phone">Phone</Label>
                  <PhoneInput
                    id="c-phone"
                    name="phone"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v || "" })}
                    defaultCountry="IN"
                    placeholder="Enter phone number"
                    data-testid="contact-input-phone"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="c-subject">Subject</Label>
                  <Input id="c-subject" name="subject" value={form.subject} onChange={handleChange} data-testid="contact-input-subject" className="mt-1.5" placeholder="How can we help?" />
                </div>
              </div>
              <div>
                <Label htmlFor="c-message">Message *</Label>
                <Textarea id="c-message" name="message" value={form.message} onChange={handleChange} data-testid="contact-input-message" required className="mt-1.5" rows={6} placeholder="Tell us a little about your project, workflow, or question…" />
              </div>
              <Button type="submit" disabled={loading} data-testid="contact-submit-btn" className="h-12 rounded-md bg-[#0046B5] px-6 text-white hover:bg-[#003A99]">
                {loading ? "Sending…" : "Send Message"}
              </Button>
            </form>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;
